import React, { FunctionComponent, useEffect } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { useNavigate } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { useUpgradeDialogs } from 'src/shared/dialogs';
import { T, backofficeUrl, stringFormat } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { CustomCatImage } from 'src/shared/svg/podCustomCatNewLogo';
import { InvitionImage } from 'src/shared/svg/podInvitionLogo';
import PrintfulLogo from 'src/shared/svg/podPrintfulLogo';
import styled from 'styled-components';
import { useDialogManager } from '../../../../widgets/dialog';
import { ContainerBase } from '../../../../widgets/layouts';
import { PageHeaderContainer } from '../../../../widgets/pageHeader';
import { Link } from '../../../../widgets/buttons';
import { providerAction, setHeaderDescriptionCustomComponent, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { ProductProvider, providerSelectionQuery } from './__generated__/providerSelectionQuery.graphql';
import { ProviderSetupDialog } from './providerSetupDialog';

const ProviderCardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
`;

const ProviderCardButton = styled.button<{ $brandColor?: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 24px;
    border: 1px solid ${(props) => props.$brandColor || props.theme.app.borderColor};
    border-left: 3px solid ${(props) => props.$brandColor || props.theme.app.borderColor};
    border-radius: 8px;
    background: none;
    text-align: left;
    cursor: pointer;
    min-height: 180px;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

    &:hover {
        border-color: ${(props) => props.$brandColor || props.theme.app.secondary};
        border-left-color: ${(props) => props.$brandColor || props.theme.app.secondary};
        box-shadow: 0 2px 12px ${(props) => (props.$brandColor ? props.$brandColor + '22' : 'rgba(0, 0, 0, 0.08)')};
    }
`;

const ProviderCardLogoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 12px;
    margin-bottom: 20px;

    svg,
    img {
        height: 30px;
        width: auto;
        max-width: 120px;
        object-fit: contain;
    }
`;

const ProviderCardName = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    font-family: ${(props) => props.theme.app.font};
    margin-bottom: 4px;
`;

const ProviderCardDescription = styled.span`
    font-size: 13px;
    color: #6b6b6b;
    font-family: ${(props) => props.theme.app.font};
    flex: 1;
`;

const ProviderCardArrow = styled.span`
    font-size: 18px;
    color: ${(props) => props.theme.app.secondary};
    margin-top: 2px;
    align-self: flex-end;
`;

const CustomIntegrationFooter = styled.div`
    font-size: 13px;
    color: #6b6b6b;
    font-family: ${(props) => props.theme.app.font};
`;

const ProviderCardBadge = styled.span<{ $integrated: boolean }>`
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    background: ${(props) => (props.$integrated ? '#e8f5e9' : '#f5f5f5')};
    color: ${(props) => (props.$integrated ? '#2e7d32' : '#9e9e9e')};
`;

const providersIds: Record<ProductProvider, number> = {
    UNDEFINED: -1,
    ZAKEKE: 0,
    PRINTFUL: 1,
    CUSTOM_CAT: 2,
    INVITION: 3,
    PRINTFUL_DIRECT: 4,
    HP: 5,
    VERSORI: 6,
    CASA_DE_LAS_CARCASAS: 7,
    FAVORITE_GIFT: 8
};

const providerInfo: Record<string, { name: string; description: string; brandColor: string; icon: React.ReactNode }> = {
    PRINTFUL: {
        name: 'Printful',
        description:
            'Printful is one of the largest print-on-demand drop shipping services in the world. Choose from more than 250 products, from clothing and accessories to home decor.',
        brandColor: '#EE4B2B',
        icon: <PrintfulLogo />
    },
    CUSTOM_CAT: {
        name: 'CustomCat',
        description: 'Very popular print on demand company for online merchants. Catalog includes clothing, bags, hats, accessories, jewelry, and drinkware.',
        brandColor: '#1E73BE',
        icon: <CustomCatImage />
    },
    INVITION: {
        name: 'Printeers',
        description: 'Print-on-demand drop shipping company specialized in phone cases with a catalog of over 450 products for more than 100 phone models.',
        brandColor: '#4A1FA0',
        icon: <InvitionImage />
    }
};

const ProviderSelection: FunctionComponent<{}> = () => {
    const { showDialog, closeDialog } = useDialogManager();
    const { dispatch, state } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const { showPlanUpgradeDialog } = useUpgradeDialogs(); // Use the custom hook
    const { sellerMarketplace } = useSubscriptionData();

    const navigate = useNavigate();

    const query = useLazyLoadQuery<providerSelectionQuery>(
        graphql`
            query providerSelectionQuery($salesChannelID: ID!) {
                supportedProviders(salesChannelID: $salesChannelID)
                hasPrintfulKey: hasValidProviderKey(provider: PRINTFUL)
                hasInvitionKey: hasValidProviderKey(provider: INVITION)
                hasCustomCatKey: hasValidProviderKey(provider: CUSTOM_CAT)
                subscription {
                    id
                    maxProducts {
                        pricingVersion
                        canAddProduct
                        maxProducts
                        numProducts
                        numPublishedProducts
                    }
                }
            }
        `,
        {
            salesChannelID: state.salesChannelID
        }
    );

    const availableKeys: Record<ProductProvider, boolean> = {
        UNDEFINED: false,
        ZAKEKE: false,
        PRINTFUL: query.hasPrintfulKey,
        CUSTOM_CAT: query.hasCustomCatKey,
        INVITION: query.hasInvitionKey,
        PRINTFUL_DIRECT: false,
        HP: false,
        VERSORI: false,
        CASA_DE_LAS_CARCASAS: false,
        FAVORITE_GIFT: false
    };

    const handleProviderClick = (provider: ProductProvider) => {
        if (query.subscription?.maxProducts.pricingVersion === 'VERSION_2') {
            const numCurrentPublishedProducts = query.subscription?.maxProducts.numPublishedProducts ?? 0;
            const numMaxPublishedProducts = query.subscription?.maxProducts.maxProducts ?? 0;
            if (numCurrentPublishedProducts >= numMaxPublishedProducts) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            console.log('Upgrade completato, aggiungere callback caso providerSelection');
                        }}
                    />
                );
                return;
            }
        }

        if (availableKeys[provider] === true) {
            dispatch(providerAction(provider));
            changeStep('import-catalog');
        } else {
            const providerNames: Record<string, string> = {
                PRINTFUL: 'Printful',
                CUSTOM_CAT: 'CustomCat',
                INVITION: 'Printeers'
            };
            showDialog(
                'provider-setup',
                <ProviderSetupDialog
                    providerId={providersIds[provider]}
                    providerName={providerNames[provider] ?? ''}
                    onClose={() => closeDialog('provider-setup')}
                    onIntegrated={() => {
                        closeDialog('provider-setup');
                        const providerSlugs: Record<ProductProvider, string | undefined> = {
                            PRINTFUL: 'printful',
                            CUSTOM_CAT: 'customcat',
                            INVITION: 'printeers',
                            UNDEFINED: undefined,
                            ZAKEKE: undefined,
                            PRINTFUL_DIRECT: undefined,
                            HP: undefined,
                            VERSORI: undefined,
                            CASA_DE_LAS_CARCASAS: undefined,
                            FAVORITE_GIFT: undefined
                        };
                        const slug = providerSlugs[provider];
                        window.location.href = slug
                            ? backofficeUrl(`/integration/provider-configuration-page/${slug}`)
                            : backofficeUrl('/integration/provider-configuration-page');
                    }}
                />
            );
        }
    };

    // Page title
    useEffect(() => {
        dispatch(
            setHeaderDescriptionCustomComponent(
                <PageHeaderContainer.TitleDescription>
                    <PageHeaderContainer.Title>{T._('Print on demand', 'Admin')}</PageHeaderContainer.Title>
                </PageHeaderContainer.TitleDescription>
            )
        );

        return () => {
            dispatch(setHeaderDescriptionCustomComponent(null));
        };
    }, []);

    return (
        <>
            <ContainerBase>
                <Section title={T._('Choose supplier', 'Admin')}>
                    <ProviderCardsGrid style={{ gridTemplateColumns: `repeat(${query.supportedProviders.filter((x) => x && providerInfo[x]).length}, 1fr)` }}>
                        {query.supportedProviders
                            .filter((x) => x && providerInfo[x])
                            .map((x) => {
                                const info = providerInfo[x];
                                const isIntegrated = availableKeys[x] ?? false;
                                return (
                                    <ProviderCardButton key={x} $brandColor={info.brandColor} onClick={() => handleProviderClick(x)}>
                                        <ProviderCardLogoContainer>{info.icon}</ProviderCardLogoContainer>
                                        <ProviderCardBadge $integrated={isIntegrated}>
                                            {isIntegrated ? T._('Integrated', 'Admin') : T._('Not integrated', 'Admin')}
                                        </ProviderCardBadge>
                                        <ProviderCardName>{info.name}</ProviderCardName>
                                        <ProviderCardDescription>{T._(info.description, 'Admin')}</ProviderCardDescription>
                                        <ProviderCardArrow>{'\u2192'}</ProviderCardArrow>
                                    </ProviderCardButton>
                                );
                            })}
                    </ProviderCardsGrid>
                    <CustomIntegrationFooter>
                        {T._('Wish to integrate your preferred POD? ', 'Admin')}
                        <Link target='_blank' href={'https://zakeke.zendesk.com/hc/en-us/articles/360019068580-Integrating-with-your-preferred-POD-supplier'}>
                            {T._('read how to make it', 'Admin')}
                        </Link>
                        .
                    </CustomIntegrationFooter>
                </Section>
            </ContainerBase>
        </>
    );
};

export default ProviderSelection;
